import { Button } from "@components/ui/Button"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import * as D from "@radix-ui/react-dialog"
import { cn } from "@utils/tw"
import { animate, AnimatePresence, AnimatePresenceProps, AnimationProps, motion, useMotionValue } from "motion/react"
import { PropsWithChildren, useEffect, useMemo, useState } from "react"
import { FLYING_CARPET_PANELS, FlyingCarpetTabInfo } from "./carpet-panels"
import { createPortal } from "react-dom"

/**
 * On desktop, we're using a radix dialog box, which has better accessibility and focus management.
 */
export function FlyingCarpetDesktop() {
  const carpet = useFlyingCarpet()
  const [dialog, setDialog] = useState<HTMLDivElement | null>(null)

  const arrowLeft = useMotionValue("600px")

  const header = useMemo(() => {
    return document.querySelector("#site-header")
  }, [])
  const anchor = useMemo(() => {
    if (!carpet.tab) return null
    return document.querySelector(`#flying-carpet-anchor-${carpet.tab}`)
  }, [carpet.tab])

  useEffect(() => {
    if (!header || !dialog) {
      return
    }
    const update = () => {
      /**
       * Calculate the top position for the panel, as well as the arrow position
       */
      const headerBounds = header.getBoundingClientRect()
      const anchorBounds = anchor?.getBoundingClientRect()
      const dialogBounds = dialog.getBoundingClientRect()
      const arrow = anchorBounds
        ? anchorBounds.left + anchorBounds.width / 2 - (headerBounds.width - dialogBounds.width)
        : headerBounds.width

      animate(arrowLeft, arrow + "px", {
        type: "spring",
        stiffness: 100,
        damping: 15,
      })
    }

    // Observe the header and anchor bounds
    const ro = new ResizeObserver(update)
    ro.observe(header)
    ro.observe(dialog)
    if (anchor) ro.observe(anchor)

    return () => ro.disconnect()
  }, [carpet.isOpen, header, anchor, dialog])

  const panel = carpet.tab ? FLYING_CARPET_PANELS[carpet.tab] : null

  return (
    <D.Root
      open={carpet.isOpen}
      onOpenChange={(open) => {
        if (!open) carpet.close()
      }}
    >
      <AnimatePresence>
        {carpet.isOpen && (
          <motion.div
            className="radix-backdrop z-40 bg-[rgba(0,0,0,0.3)] will-change-transform"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onPointerDown={() => carpet.close()}
          />
        )}
      </AnimatePresence>
      <D.Portal>
        <D.Content
          ref={setDialog}
          className={cn(
            "fixed top-flyingCarpetTop left-auto right-0 bottom-0 z-50",
            "will-change-transform",
            "flex items-stretch justify-stretch",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full",
            "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-full",
            "!duration-500 transition-none",
            "w-[100vw] sm:w-[25vw] sm:min-w-[440px]",
            "!pointer-events-none outline-none",
          )}
        >
          <div className="absolute invisible select-none pointer-events-none">
            <D.Title>{panel?.title}</D.Title>
            <D.Description>{panel?.description}</D.Description>
          </div>
          {/* Arrow */}
          <motion.div
            className="absolute top-[--arrow-top] left-[--arrow-left] -translate-x-1/2 -translate-y-full z-10"
            style={{ "--arrow-left": arrowLeft } as any}
          >
            <svg
              className="fill-bg-mid rotate-180"
              width="10"
              height="5"
              viewBox="0 0 30 10"
              preserveAspectRatio="none"
              aria-hidden
            >
              <polygon points="0,0 30,0 15,10"></polygon>
            </svg>
          </motion.div>

          {/* Modal content */}
          <div className="w-full outline-none [--padding:var(--tw-grid-margin)] md:[--padding:var(--spacing-5)] border-t md:border border-bg-mid bg-bg text-fg pointer-events-auto z-20 overflow-hidden">
            {panel && (
              <div className="h-full w-full flex flex-col">
                {/* Top bar */}
                <AnimatePresence mode="wait" initial={false}>
                  {panel.hasHeader !== false && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="type-title-m h-[calc(var(--spacing-headerRowHeight)+1px)] pl-[--padding] pr-3 relative border-b border-bg-mid flex-none overflow-hidden flex items-center"
                    >
                      {/* Title */}
                      <Ticker tickerKey={panel.title} className="flex flex-1 items-center h-full">
                        <div className="h-full flex items-center">{panel.title}</div>
                      </Ticker>

                      <div className="flex flex-1 items-center justify-end gap-2">
                        <AnimatePresence>
                          {panel.widget && (
                            <motion.div
                              key={panel.title}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <panel.widget />
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Close Button */}
                        <D.Close asChild>
                          <Button icon="cross" variant="ghost" shape="icon" />
                        </D.Close>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Main section */}
                <div className="flex-1 overflow-hidden flex items-center relative">
                  <Ticker tickerKey={carpet.tab!} animation="fade" className="absolute inset-0">
                    <FlyingCarpetContent tab={panel} />
                  </Ticker>
                </div>
              </div>
            )}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

function FlyingCarpetContent(props: { tab: FlyingCarpetTabInfo }) {
  const Component = props.tab.component

  if (Component) {
    return <Component />
  } else {
    return null
  }
}

const animationStyles = {
  ticker: {
    initial: { transform: "translateY(-60px)" },
    animate: { transform: "translateY(0px)" },
    exit: { transform: "translateY(60px)" },
    transition: { type: "spring", stiffness: 100, damping: 20 },
  },
  fade: {
    initial: { opacity: 0, y: "-1%", scale: 0.95 },
    animate: { opacity: 1, y: "0%", scale: 1, transition: { bounce: 0.2, delay: 0.1 } },
    exit: { opacity: 0, y: "1%", scale: 0.95, transition: { duration: 0.1 } },
  },
} satisfies Record<string, AnimationProps>

function Ticker(
  props: PropsWithChildren<{
    tickerKey: string | number
    mode?: AnimatePresenceProps["mode"]
    animation?: keyof typeof animationStyles
    className?: string
  }>,
) {
  const animProps = animationStyles[props.animation ?? "ticker"]

  const animationKey = useMemo(() => props.tickerKey + "_" + Date.now(), [props.tickerKey])

  return (
    <div className={props.className}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div key={animationKey} {...animProps} className="w-full h-full">
          {props.children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
